import { Fragment } from 'react';
import clsx from 'clsx';

/**
 * Reusable reporting heatmap (Prompt F foundation). Follows the in-house
 * ArtistAvailabilityHeatmap approach - a grid of square cells with tone-scaled
 * intensity - rather than a chart-library heatmap. Stage 4 uses it for the
 * inquiry-arrival (hour x weekday) and rebook (stores x days) heatmaps.
 *
 * Intensity is a theme-token rgb with a data-driven alpha, so cells stay on the
 * gold/cream/ink palette and remain theme-aware.
 */

export type HeatmapTone = 'gold' | 'blood' | 'mint';

const TONE_RGB: Record<HeatmapTone, string> = {
  gold: 'var(--accent-gold)',
  blood: 'var(--accent-blood)',
  mint: 'var(--accent-mint)',
};

export interface HeatmapColumn {
  key: string;
  label: string;
  /** Optional second line under the label (e.g. a date). */
  sub?: string;
}

export interface HeatmapCellData {
  colKey: string;
  value: number;
  /** Hover title; defaults to the value. */
  title?: string;
}

export interface HeatmapRowData {
  label: string;
  cells: readonly HeatmapCellData[];
}

interface Props {
  columns: readonly HeatmapColumn[];
  rows: readonly HeatmapRowData[];
  /** Intensity denominator; defaults to the largest cell value. */
  max?: number;
  tone?: HeatmapTone;
  /** Render the numeric value inside each cell. */
  showValues?: boolean;
  caption?: string;
  emptyLabel?: string;
}

function cellBackground(value: number, max: number, tone: HeatmapTone): string {
  if (max <= 0 || value <= 0) return 'rgb(var(--surface-3) / 0.35)';
  const alpha = 0.15 + 0.6 * Math.min(1, value / max);
  return `rgb(${TONE_RGB[tone]} / ${alpha.toFixed(2)})`;
}

export default function ReportHeatmap({
  columns,
  rows,
  max,
  tone = 'gold',
  showValues = true,
  caption,
  emptyLabel = 'No data yet.',
}: Props) {
  const dataMax = max ?? Math.max(0, ...rows.flatMap((r) => r.cells.map((c) => c.value)));

  if (rows.length === 0) {
    return (
      <p className="font-serif text-cream-soft text-center py-4" data-testid="heatmap-empty">
        {emptyLabel}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="report-heatmap">
      <table className="text-[10px] font-mono">
        <thead>
          <tr>
            <th className="text-left text-cream-faint font-normal pr-2 py-1 whitespace-nowrap" />
            {columns.map((col) => (
              <th key={col.key} className="text-cream-faint font-normal px-0.5 py-1 text-center">
                <div className="leading-none">{col.label}</div>
                {col.sub ? <div className="leading-none mt-0.5 text-gold-dim">{col.sub}</div> : null}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const byCol = new Map(row.cells.map((c) => [c.colKey, c]));
            return (
              <Fragment key={row.label}>
                <tr>
                  <td className="pr-2 py-0.5 text-cream-soft whitespace-nowrap">
                    <span className="truncate max-w-[130px] inline-block align-middle">
                      {row.label}
                    </span>
                  </td>
                  {columns.map((col) => {
                    const cell = byCol.get(col.key);
                    const value = cell?.value ?? 0;
                    return (
                      <td key={col.key} className="px-0.5 py-0.5">
                        <div
                          data-testid={`heatmap-cell-${row.label}-${col.key}`}
                          title={cell?.title ?? `${row.label} · ${col.label}: ${value}`}
                          className={clsx(
                            'w-7 h-7 rounded-sm flex items-center justify-center border border-transparent',
                            value > 0 ? 'text-ink-black' : 'text-cream-faint/40',
                          )}
                          style={{ background: cellBackground(value, dataMax, tone) }}
                        >
                          {showValues ? value : ''}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {caption ? (
        <p className="font-inter text-[10px] text-cream-faint uppercase tracking-widest mt-2">
          {caption}
        </p>
      ) : null}
    </div>
  );
}
